"use client";

import {
  ArithmeticProblemView,
  equationText,
} from "@/components/tools/worksheets-math/arithmetic-view";
import type { WorksheetCommonStrings } from "@/components/tools/worksheets-math/common-strings";
import {
  AnswerList,
  type CommonSettings,
  ProblemCell,
  ProblemGrid,
  WorksheetSheets,
} from "@/components/tools/worksheets-math/worksheet-kit";
import {
  type ArithmeticProblem,
  problemWidth,
} from "@/lib/tool-kit/worksheets/arithmetic";
import { suggestedColumns } from "@/lib/tool-kit/worksheets/layout";

/** The printable sheet and answer key for any list of arithmetic problems. */
export function ArithmeticSheet({
  problems,
  settings,
  strings,
  title,
  seed,
  workRows,
  withRemainder = false,
  scaffoldBoxes = 0,
}: {
  problems: readonly ArithmeticProblem[];
  settings: CommonSettings;
  strings: WorksheetCommonStrings;
  title: string;
  seed: number;
  workRows: number;
  withRemainder?: boolean;
  scaffoldBoxes?: number;
}) {
  const width = problemWidth(problems, settings.layout);
  const columns =
    settings.columns > 0
      ? settings.columns
      : suggestedColumns(settings.layout, width);
  const gap =
    settings.layout === "vertical" ? "gap-x-6 gap-y-6" : "gap-x-8 gap-y-2";
  const keyColumns = Math.max(
    1,
    Math.min(4, Math.floor(60 / (width + 14)) + 1)
  );
  return (
    <WorksheetSheets
      answers={
        <AnswerList
          columns={keyColumns}
          entries={problems.map((p) => equationText(p, strings.remainderAbbr))}
        />
      }
      problems={
        <ProblemGrid columns={columns} gap={gap}>
          {problems.map((problem, index) => (
            <ProblemCell
              // biome-ignore lint/suspicious/noArrayIndexKey: problems are positional
              key={index}
              number={settings.numbered ? index + 1 : null}
            >
              <ArithmeticProblemView
                layout={settings.layout}
                problem={problem}
                remainderLabel={strings.remainderAbbr}
                scaffoldBoxes={scaffoldBoxes}
                withRemainder={withRemainder}
                workRows={workRows}
              />
            </ProblemCell>
          ))}
        </ProblemGrid>
      }
      seed={seed}
      settings={settings}
      strings={strings}
      title={title}
    />
  );
}
